import {
  adobeAnalyticsPageLoad,
  fireAdobeAnalyticsEvent,
  adobeCleanString,
  handleFileDownloadEvent,
  analyticsCtaDataObject,
} from '../../helpers/adobe-analytics';
import { createFormAnalytics } from '../../helpers/form-analytics';
import { createToolAnalytics } from '../../helpers/tool-analytics';

// Generic viewer events (emit-interaction-events) mapped onto the shared
// tool vocabulary. Anything not listed only counts toward engaged time.
const INTERACTION_HANDLERS = {
  'configure:material': (tool, data) =>
    tool.select('Material', data.materialName),
  'configure:part': (tool, data) => tool.select('Part', data.partName),
  'configure:layout': (tool, data) => tool.select('Layout', data.layoutName),
  'configure:variant': (tool, data) => tool.select('Variant', data.name),
  'configure:open': tool => tool.view('Configurator'),
  'camera:rotate': tool => tool.view('Orbit'),
  'camera:zoom': tool => tool.view('Zoom'),
  'camera:reset': tool => tool.view('Reset'),
  'camera:fullscreen': tool => tool.view('Fullscreen'),
  'media:image-modal': tool => tool.view('Image'),
  'cta:ar': tool => tool.view('AR'),
  'cta:request-quote-open': (tool, data) =>
    tool.action('Request a Quote', data.variantName),
  'cta:add-to-cart': (tool, data) =>
    tool.action('Add to Cart', data.variantName),
  'cta:share': tool => tool.action('Share'),
  'cta:screenshot': tool => tool.action('Save Image'),
  'cta:export': tool => tool.action('Export'),
  'cta:undo': tool => tool.edit('Undo'),
  'cta:redo': tool => tool.edit('Redo'),
};

// Configure events also fire as the product loads, so they count only after
// the visitor's first touch.
const trackGenericInteractions = (viewer, toolAnalytics) => {
  let touched = false;
  const markTouched = () => {
    touched = true;
  };
  viewer.addEventListener('pointerdown', markTouched, { passive: true });
  viewer.addEventListener('keydown', markTouched, { passive: true });

  viewer.addEventListener('bitreel-interaction', event => {
    const { category, action, data = {} } = event.detail || {};
    if (category === 'configure' && !touched) return;
    const handler = INTERACTION_HANDLERS[`${category}:${action}`];
    if (handler) handler(toolAnalytics, data);
    else toolAnalytics.activity();
  });
};

// Until the viewer has emit-interaction-events, its own element events cover
// variant changes, orbit, AR, add to cart and the image modal. Variant
// updates also fire as the product loads, so only those after the visitor's
// first touch count as choices.
const trackElementEvents = (viewer, toolAnalytics) => {
  let touched = false;
  let pointerDown = false;
  let cameraMovedWhileDown = false;
  let variantName = '';

  const markTouched = () => {
    touched = true;
  };
  viewer.addEventListener('pointerdown', markTouched, { passive: true });
  viewer.addEventListener('keydown', markTouched, { passive: true });

  viewer.addEventListener('variant_update', event => {
    variantName = event.detail?.name || '';
    if (touched && variantName) toolAnalytics.select('Variant', variantName);
  });

  viewer.addEventListener('mouse_state_change', event => {
    pointerDown = !!event.detail?.mouseDownState;
    if (pointerDown) cameraMovedWhileDown = false;
    else if (cameraMovedWhileDown) toolAnalytics.view('Orbit');
  });

  // Also fires for the viewer's own camera animations; only a move while
  // the pointer is down is the visitor's.
  viewer.addEventListener('cameramoved', () => {
    if (pointerDown) cameraMovedWhileDown = true;
  });

  viewer.addEventListener('button_clicked', event => {
    const { type } = event.detail || {};
    if (type === 'add_to_cart')
      toolAnalytics.action('Add to Cart', variantName);
    if (type === 'ar') toolAnalytics.view('AR');
  });

  viewer.addEventListener('imageModalOpened', event => {
    if (event.detail?.isOpen) toolAnalytics.view('Image');
  });
};

const setupVisualizerToolAnalytics = () => {
  const viewer = document.querySelector(
    '#bitreel-visualizer bitreel-model-viewer'
  );
  if (!viewer) return null;

  const toolAnalytics = createToolAnalytics('Bitreel Visualizer');
  toolAnalytics.observe(viewer);
  if (viewer.hasAttribute('emit-interaction-events')) {
    trackGenericInteractions(viewer, toolAnalytics);
  } else {
    trackElementEvents(viewer, toolAnalytics);
  }
  return toolAnalytics;
};

// Turn { field_x: {label, value} } into { [label]: value }
const normalizeLabeledFields = source => {
  // If it's already flat, just return it
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};

  const values = Object.values(source);

  // Detect the "field_*" shape: every item looks like { label, value }
  const looksLikeLabeledFields =
    values.length > 0 &&
    values.every(
      v => v && typeof v === 'object' && 'label' in v && 'value' in v
    );

  if (!looksLikeLabeledFields) return source;

  return values.reduce((acc, { label, value }) => {
    if (label) acc[label] = value ?? '';
    return acc;
  }, {});
};

const isPlainObject = v =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

// Recursive schema-to-source mapper
const mapBySchema = (source, schema) => {
  const normalizedSource = normalizeLabeledFields(source);

  if (typeof schema === 'function') {
    return schema(normalizedSource) ?? '';
  }

  if (isPlainObject(schema)) {
    return Object.fromEntries(
      Object.entries(schema).map(([key, childSchema]) => [
        key,
        mapBySchema(normalizedSource, childSchema),
      ])
    );
  }

  if (typeof schema === 'string') {
    return normalizedSource[schema] ?? '';
  }

  return schema;
};

const configuratorLeadsSchema = () => {
  return {
    input: {
      leadAttributes: {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        company: 'Company Name',
        street: 'Address Line 1',
        street2: 'Address Line 2 (optional)',
        city: 'City',
        state: 'State/Prov/Region',
        country: 'Country',
        zipcode: 'Zip/Postal Code',
        phone: 'Phone',
        projectName: 'Project Name',
        installAddressAttributes: 'Project Installation Zip Code',
      },
    },
  };
};

const setupVisualizationEventListener = () => {
  // Bitreel sends no event when its quote form opens or is typed in, so this
  // form reports only its outcome.
  const formAnalytics = createFormAnalytics('Contact|Visualizer Quote');
  const toolAnalytics = setupVisualizerToolAnalytics();

  const postConfiguratorLead = async payload => {
    try {
      const response = await fetch('/add-marketing-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded ${response.status}: ${errorText}`);
      }

      const json = await response.json();
      console.info('Lead successfully submitted:', json);
      return json;
    } catch (err) {
      console.error('Failed to submit digital-print lead:', err);
      throw err;
    }
  };

  const getPresignedUrls = async quote_id => {
    const res = await fetch(
      `https://bitscene-api.bitreel.com/api/quotes/${quote_id}/presigned-urls`
    );

    if (!res.ok) {
      throw new Error(`Request failed: ${res.status}`);
    }

    const data = await res.json();
    const screenshotUrl = data?.screenshot_url ?? null;
    return screenshotUrl;
  };

  document.addEventListener('download-design-package-clicked', async event => {
    try {
      const { visualizerOutput } = event.detail;
      // TODO: ADD PRODUCT INFO ONCE BITREEL HAS IT READY
      const analyticsData = {
        file_format: 'zip',
        file_name: 'Bitreel Design Package',
        link_url: 'dynamic_design_package.zip',
        link_type: 'PACKAGE',
        placement: 'Bitreel Visualizer',
      };
      handleFileDownloadEvent(analyticsCtaDataObject(analyticsData));
      toolAnalytics?.activity();
    } catch (err) {
      console.error('download-design-package-clicked handler failed:', err);
    }
  });

  document.addEventListener('dynamic-quote-form-submitted', async event => {
    toolAnalytics?.flush();
    try {
      const { formData, configuratorOutput, quote_id } = event.detail;

      const userSelectionsSummary =
        configuratorOutput?.currentSelectionsSummary || [];

      const userSelections = formattedConfiguratorSelectionsSummary(
        userSelectionsSummary,
        quote_id
      );

      const presignedUrl = await getPresignedUrls(quote_id);

      const userNotes =
        Object.values(formData).find(f => f?.label === 'Additional Notes')
          ?.value ?? '';

      const mapped = mapBySchema(formData, configuratorLeadsSchema()).input;

      // If install address is provided it needs to be nested in it's own object
      // for My3form's lead dashboard, otherwise it should not be defined.
      // Bitreel's cms does not provide that data structure so we handle it here.
      const installAddress = mapped.leadAttributes.installAddressAttributes;
      if (installAddress) {
        mapped.leadAttributes.installAddressAttributes = {
          zipcode: installAddress,
        };
      } else {
        delete mapped.leadAttributes.installAddressAttributes;
      }

      mapped.leadAttributes = {
        ...mapped.leadAttributes,
        source: 'visualization_lead',
      };

      const existingVizObj = mapped.visualizationAttributes ?? {};

      // Opacity, materialType and panelOrientation are added here to pass
      // validations in My3form's api.
      mapped.visualizationAttributes = {
        ...existingVizObj,
        leadType: 'Hardware System',
        configurationUrl:
          existingVizObj.configurationUrl ??
          'https://www.3-form.com/configurator',
        configuredImageUrl: presignedUrl,
        visualizationDetails: userSelections,
        customInstructions: userNotes,
        opacity: '1',
        materialType: 'Varia',
        panelOrientation: 'not applicable',
      };

      const success = await postConfiguratorLead(mapped)
        .then(() => true)
        .catch(() => false);

      if (success) formAnalytics.submit();
      else formAnalytics.error();
    } catch (err) {
      formAnalytics.error();
      console.error('dynamic-quote-form-submitted handler failed:', err);
    }
  });
};

const formattedConfiguratorSelectionsSummary = (
  currentSelectionSummary,
  quote_id
) => {
  currentSelectionSummary = currentSelectionSummary || [];
  const userSelections = currentSelectionSummary.reduce((acc, item) => {
    if (!item.partGroupsMetadata) return acc;
    const labelKey = item.label.replace(/\s+/g, '_');

    // Build inner object for each selection
    const groupsObj = item.partGroupsMetadata.reduce((groupAcc, group) => {
      const groupKey = group.name.replace(/\s+/g, '_');
      const value = group.materialVisible ? group.materialName : group.partName;

      groupAcc[groupKey] = value;
      return groupAcc;
    }, {});

    acc[labelKey] = groupsObj;
    return acc;
  }, {});

  userSelections['bitreelQuoteId'] = quote_id;

  return userSelections;
};

export default {
  init: setupVisualizationEventListener,
};
